// ==================== PAYMENT CANCEL PAGE ====================

import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { XCircle, ArrowLeft, HelpCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Logo } from '../../components/ui/Logo';
import { fadeInUp, staggerContainer } from '../../utils/animations';

const PaymentCancelPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-(--color-dark-navy)">
      {/* Header */}
      <header className="bg-(--color-midnight-navy)/95 backdrop-blur-xl border-b border-(--color-border-gray)">
        <div className="container-custom flex items-center justify-center h-18 py-3">
          <Logo size="lg" />
        </div>
      </header>

      {/* Main Content */}
      <main className="container-custom py-16">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="max-w-xl mx-auto text-center"
        >
          {/* Cancel Icon */}
          <motion.div variants={fadeInUp} className="mb-8">
            <div className="w-24 h-24 rounded-full bg-(--color-error)/10 
                            flex items-center justify-center mx-auto border-2 border-(--color-error)/20">
              <XCircle size={48} className="text-(--color-error)" />
            </div>
          </motion.div>

          {/* Message */}
          <motion.div variants={fadeInUp}>
            <h1 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-4">
              Payment Cancelled
            </h1>
            <p className="text-lg text-(--color-soft-gray) mb-8">
              No worries! Your payment was cancelled and you haven't been charged. 
              You can try again whenever you're ready.
            </p>
          </motion.div>

          {/* Reasons Card */}
          <motion.div
            variants={fadeInUp}
            className="bg-(--color-navy-light) border border-(--color-border-gray) rounded-2xl p-6 mb-8 text-left"
          >
            <div className="flex items-center gap-3 mb-4">
              <HelpCircle size={20} className="text-(--color-signal-orange)" />
              <h2 className="text-lg font-semibold text-white">Common reasons for cancellation</h2>
            </div>
            <ul className="space-y-3 text-(--color-soft-gray)">
              <li className="flex items-start gap-2">
                <span className="text-(--color-signal-orange) mt-1">•</span>
                <span>Need more time to compare plans</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-(--color-signal-orange) mt-1">•</span>
                <span>Questions about features or pricing</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-(--color-signal-orange) mt-1">•</span>
                <span>Payment method issues</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-(--color-signal-orange) mt-1">•</span>
                <span>Accidental cancellation</span>
              </li>
            </ul>
          </motion.div>

          {/* Action Buttons */}
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="primary"
              size="lg"
              onClick={() => navigate('/app/select-plan')}
              leftIcon={<ArrowLeft size={18} />}
            >
              Return to Plans
            </Button>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => window.open('mailto:support@amplifymsg.com')}
            >
              Contact Support
            </Button>
          </motion.div>

          {/* Help Link */}
          <motion.p variants={fadeInUp} className="text-sm text-(--color-muted-gray) mt-8">
            Have questions about our plans?{' '}
            <a href="/pricing" className="text-(--color-signal-orange) hover:underline">
              View pricing details
            </a>
          </motion.p>
        </motion.div>
      </main>
    </div>
  );
};

export { PaymentCancelPage };

